import React, { useEffect, useRef, useCallback } from 'react';
import jSuites from 'jsuites';
import jspreadsheet from 'jspreadsheet-ce';
import 'jspreadsheet-ce/dist/jspreadsheet.css';
import 'jsuites/dist/jsuites.css';

// jspreadsheet-ce expects global jSuites
if (typeof window !== 'undefined') {
  window.jSuites = window.jSuites || jSuites;
}

/**
 * Google Sheets–like grid powered by jspreadsheet-ce (Excel formulas, copy/paste, resize).
 */
export default function SheetEditor({
  rows,
  editable = true,
  onChange,
  minCols = 16,
  minRows = 40,
}) {
  const hostRef = useRef(null);
  const sheetRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const readyRef = useRef(false);

  const padData = useCallback((raw) => {
    const data = (raw || [[]]).map((r) => (r || []).map((c) => (c == null ? '' : String(c))));
    const cols = Math.max(minCols, ...data.map((r) => r.length), 1);
    while (data.length < minRows) data.push(Array(cols).fill(''));
    return data.map((r) => {
      const next = [...r];
      while (next.length < cols) next.push('');
      return next;
    });
  }, [minCols, minRows]);

  useEffect(() => {
    if (!hostRef.current) return undefined;

    // Destroy previous
    if (sheetRef.current) {
      try {
        jspreadsheet.destroy(hostRef.current, true);
      } catch (_) { /* ignore */ }
      sheetRef.current = null;
      hostRef.current.innerHTML = '';
    }

    const data = padData(rows);
    const colCount = data[0]?.length || minCols;
    const columns = Array.from({ length: colCount }, () => ({
      type: 'text',
      width: 110,
      align: 'left',
    }));

    const instance = jspreadsheet(hostRef.current, {
      data,
      columns,
      allowInsertRow: editable,
      allowInsertColumn: editable,
      allowDeleteRow: editable,
      allowDeleteColumn: editable,
      allowRenameColumn: false,
      columnSorting: editable,
      columnDrag: editable,
      rowDrag: editable,
      editable,
      tableOverflow: true,
      tableWidth: '100%',
      tableHeight: 'calc(100vh - 150px)',
      defaultColWidth: 110,
      minDimensions: [minCols, minRows],
      parseFormulas: true,
      wordWrap: true,
      copyCompatibility: true,
      about: false,
      fullscreen: false,
      toolbar: editable,
      contextMenu: editable
        ? function (obj, x, y, e, items) {
            return items;
          }
        : false,
      onchange: () => {
        if (!readyRef.current || !editable) return;
        try {
          const d = instance.getData(false);
          onChangeRef.current?.(d);
        } catch (_) { /* ignore */ }
      },
      oninsertrow: () => {
        if (!readyRef.current || !editable) return;
        try { onChangeRef.current?.(instance.getData(false)); } catch (_) {}
      },
      oninsertcolumn: () => {
        if (!readyRef.current || !editable) return;
        try { onChangeRef.current?.(instance.getData(false)); } catch (_) {}
      },
      ondeleterow: () => {
        if (!readyRef.current || !editable) return;
        try { onChangeRef.current?.(instance.getData(false)); } catch (_) {}
      },
      ondeletecolumn: () => {
        if (!readyRef.current || !editable) return;
        try { onChangeRef.current?.(instance.getData(false)); } catch (_) {}
      },
      onpaste: () => {
        if (!readyRef.current || !editable) return;
        setTimeout(() => {
          try { onChangeRef.current?.(instance.getData(false)); } catch (_) {}
        }, 50);
      },
    });

    sheetRef.current = instance;
    const hostEl = hostRef.current;
    // allow change events after init
    setTimeout(() => { readyRef.current = true; }, 100);

    return () => {
      readyRef.current = false;
      try {
        if (hostEl) jspreadsheet.destroy(hostEl, true);
      } catch (_) { /* ignore */ }
      sheetRef.current = null;
    };
    // Only re-init when rows identity from load changes (contentKey) — parent should remount via key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  // Expose getData for parent save
  useEffect(() => {
    if (hostRef.current) {
      hostRef.current.__getSheetData = () => {
        try {
          return sheetRef.current?.getData?.(false) || padData(rows);
        } catch {
          return padData(rows);
        }
      };
    }
  });

  return (
    <div className="gsheet-wrap" data-testid="drive-sheet-editor">
      <div className="gsheet-host" ref={hostRef} />
      {!editable && (
        <p className="gdrive-hint">Solo lectura — no puedes editar esta hoja con tu rol actual.</p>
      )}
    </div>
  );
}

export function readSheetData(hostEl, fallback = [['']]) {
  try {
    if (hostEl?.__getSheetData) return hostEl.__getSheetData();
  } catch (_) { /* ignore */ }
  return fallback;
}
